import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { ProfileUserSignatureService } from '../../services/profile-user-signature.service';
import { take, switchMap, filter } from 'rxjs/operators';
import { combineLatest, Subject, BehaviorSubject, of, EMPTY } from 'rxjs';
import { untilViewDestroyed } from '@sd-portal/shared/rxjs';
import { isEmpty as _isEmpty } from 'lodash';
import { MOMENT_DATE_VALUE_CONVERTER } from '../../../../../ngrx-forms/src/lib/moment-date-value-converter';
import { ESignType } from '../../models/esigntype.model';
import { ESignCategory } from '../../models/esigncategory.model';
import { UserSignature } from '../../models/user-signature.model';
import { ESignResponse, ESignArchiveRecord, EMPTY_ESIGNRESPONSE } from '../../models/esign-response.model';
import { Router } from '@angular/router';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData
} from '../confirmation-dialog/confirmation-dialog.component';
import { PermissionsService, permissionTypes } from '@sd-portal/shared/permissions';

enum ESignState {
  NotSigned,
  SignedOtherUser,
  SignedActiveCurrentUser,
  SignedInactiveCurrentUser,
  VoidInProgress,
  SignatureUpdateInProgress
}

@Component({
  selector: 'sd-esignature-sign',
  templateUrl: './esignature-sign.component.html',
  styleUrls: ['./esignature-sign.component.scss']
})
export class ESignatureSignComponent implements OnInit, OnDestroy, OnChanges {
  @Input() disabled: boolean = false;
  @Input() warning: string;
  @Input() signedType: ESignType;
  @Input() signCategory: ESignCategory;
  @Input() textPosition: 'start-line' | 'end-line' | 'below' = 'end-line';
  @Input() signResponse: ESignResponse;
  @Input() hasHistory: boolean = false;
  @Input() archives: ESignArchiveRecord[];
  @Input() legVerified: boolean = false;

  @Output() sign = new EventEmitter<number>();
  @Output() void = new EventEmitter();
  @Output() correct = new EventEmitter<number>();
  @Output() cancelSign = new EventEmitter();
  @Output() cancelVoid = new EventEmitter();
  @Output() signatureUpdate = new EventEmitter<number>();
  @Output() historyOpen = new EventEmitter<boolean>();
  @Output() routeToProfile = new EventEmitter();

  momentDateValueConverter = MOMENT_DATE_VALUE_CONVERTER;

  eSignHint: string = '';
  isPinEntered: boolean = false;
  eSignForm: FormGroup;
  confirmInProgress: boolean = false;
  showHistory: boolean = false;
  posBottom: boolean = true;

  parentInputDisable = new Subject<boolean>();
  parentInputDisable$ = this.parentInputDisable.asObservable();

  eSignatureAvail = new Subject<boolean>();
  eSignatureAvail$ = this.eSignatureAvail.asObservable();

  eSignatureAvailChecked: boolean = false;

  formCreated = new Subject<boolean>();
  formCreated$ = this.formCreated.asObservable();

  eSignatureAvailable: boolean = false;

  state: ESignState = ESignState.NotSigned;

  currState: BehaviorSubject<ESignState> = new BehaviorSubject<ESignState>(ESignState.NotSigned);
  currState$ = this.currState.asObservable();

  currentSignatureId = new Subject<number | null>();
  currentSignatureId$ = this.currentSignatureId.asObservable();

  availableSignatures = new Subject<UserSignature[] | null>();
  availableSignatures$ = this.availableSignatures.asObservable();

  isSignatureInactive: boolean = false;
  currentUserHasSigned: boolean = false;
  hasWriteAccess: boolean = false;

  notSignedState: ESignState = ESignState.NotSigned;
  activeSignatureState: ESignState = ESignState.SignedActiveCurrentUser;
  inactiveSignatureState: ESignState = ESignState.SignedInactiveCurrentUser;
  otherSignatureState: ESignState = ESignState.SignedOtherUser;
  voidState: ESignState = ESignState.VoidInProgress;
  sigUpdateState: ESignState = ESignState.SignatureUpdateInProgress;

  voidCorrectHint: string = 'Void the e-signature in progress. Enter your Pin and Confirm voiding. After the e-Signature is voided you will need to reload the page to allow changing the flight details.';
  signatureUpdateHint: string =
    'By correcting your e-Signature you will be replacing the signature with the active e-Signature on your profile.';

  private readonly promptConfirmLeave$ = () => {
    const data = {
      ...new ConfirmationDialogData(),
      message:
        'You will be taken to the User E-Signature profile page.  Any unsaved changes on this page will be lost.',
      confirmActionText: 'Yes',
      confirmButtonColor: 'warn'
    };

    const dialog = this.dialog.open(ConfirmationDialogComponent, { data });
    return dialog;
  };

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly userService: ProfileUserSignatureService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly permissionService: PermissionsService
  ) {
    combineLatest([
      this.formCreated$,
      this.parentInputDisable$,
      this.eSignatureAvail$,
      this.currState$
    ])
      .pipe(untilViewDestroyed(this))
      .subscribe(([formCreated, parentDisable, eSignAvail, state]) => {
        if (formCreated) {
          const formDisabled =
            parentDisable ||
            !eSignAvail ||
            state === ESignState.SignedInactiveCurrentUser ||
            state === ESignState.SignedActiveCurrentUser ||
            state === ESignState.SignedOtherUser;
          formDisabled ? this.eSignForm.disable() : this.eSignForm.enable();
        }
      });

    combineLatest([this.currentSignatureId$, this.availableSignatures$])
      .pipe(untilViewDestroyed(this))
      .subscribe(([currentSigId, availableSignatures]) => {
        this.determineState(currentSigId, availableSignatures);
      });

    this.currState$.pipe(untilViewDestroyed(this)).subscribe(currState => (this.state = currState));

    this.userService
      .getUserSignatures()
      .pipe(take(1))
      .subscribe(allSignatures => {
        this.eSignatureAvailChecked = true;
        const activeSignature = allSignatures.find(x => x.active);
        this.eSignatureAvailable = activeSignature != null;
        this.eSignatureAvail.next(this.eSignatureAvailable);
        this.availableSignatures.next(allSignatures);
      });
  }

  /* life-cycle */
  ngOnInit() {
    this.eSignForm = this.formBuilder.group({
      pinNumber: [
        { value: '', disabled: true },
        [Validators.required, Validators.minLength(4), Validators.maxLength(4)]
      ]
    });
    this.formCreated.next(true);
    this.parentInputDisable.next(this.disabled);
    if (this.signedType) {
      this.currentSignatureId.next(this.signedType.userSignatureId);
    }

    this.permissionService
      .currentUserCan(permissionTypes.profile.eSignature.writeAccess)
      .pipe(take(1))
      .subscribe(hasAccess => {
        this.hasWriteAccess = hasAccess;

        if (!this.hasWriteAccess) {
          this.disabled = true;
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      if (changes.disabled && !changes.disabled.firstChange) {
        this.parentInputDisable.next(changes.disabled.currentValue);
      }
      if (changes.signedType && !changes.signedType.firstChange) {
        this.eSignForm.reset();
        this.currentSignatureId.next(changes.signedType.currentValue?.userSignatureId || null);
      }
      if (changes.signResponse && !changes.signResponse.firstChange) {
        if (changes.signResponse.currentValue === EMPTY_ESIGNRESPONSE) {
          this.eSignForm.reset(); 
          this.confirmInProgress = false;
        }

        // if (!(changes.signResponse.currentValue as ESignResponse).success) {
        //   switch (this.state) {
        //     case ESignState.VoidInProgress:
        //       this.eSignHint = this.voidCorrectHint;
        //       this.confirmInProgress = true;
        //       break;
        //     case ESignState.SignatureUpdateInProgress:
        //       this.eSignHint = this.signatureUpdateHint;
        //       this.confirmInProgress = true;
        //       break;
        //     case ESignState.NotSigned:
        //       this.generateConfirmMessageForCategory();
        //       this.confirmInProgress = true;
        //       break;
        //   }
        // } else {
        //   this.eSignForm.reset();
        // }
      }
    }
  }

  determineState(currentSigId: number | null, availableSignatures: UserSignature[] | null) {
    this.isSignatureInactive = false;
    this.currentUserHasSigned = false;

    const isSigned = currentSigId !== null && currentSigId !== 0;
    if (!isSigned) {
      this.currState.next(ESignState.NotSigned);
      return;
    }

    if (availableSignatures === null) {
      this.currState.next(ESignState.SignedOtherUser);
      return;
    }

    const matchingSignature = availableSignatures.find(x => x.userSignatureId === currentSigId);
    this.currentUserHasSigned = matchingSignature !== undefined;
    if (!this.currentUserHasSigned) {
      this.currState.next(ESignState.SignedOtherUser);
      return;
    }

    this.isSignatureInactive = matchingSignature !== undefined && !matchingSignature.active;
    if (this.isSignatureInactive && this.currentUserHasSigned) {
      this.currState.next(ESignState.SignedInactiveCurrentUser);
    } else if (this.currentUserHasSigned) {
      this.currState.next(ESignState.SignedActiveCurrentUser);
    }
  }

  isSigned() {
    return this.signedType && this.signedType.userSignatureId > 0;
  }

  ngOnDestroy(): void {
    //Destroy
  }

  confirmSigning() {
    this.confirmInProgress = true;
    if (this.state === ESignState.NotSigned) {
      this.generateConfirmMessageForCategory();
    }
  }

  confirmClick() {
    this.confirmInProgress = false;
    switch (this.state) {
      case ESignState.NotSigned:
        const signPinNumber = parseInt(this.eSignForm.controls.pinNumber.value);
        this.sign.emit(signPinNumber);
        this.eSignHint = '';
        break;
      case ESignState.VoidInProgress:
        const voidPinNumber = parseInt(this.eSignForm.controls.pinNumber.value);
        this.correct.emit(voidPinNumber);
        this.eSignHint = '';
        break;
      case ESignState.SignedActiveCurrentUser:
      case ESignState.SignedInactiveCurrentUser:
      case ESignState.SignedOtherUser:
        this.confirmInProgress = true;
        this.currState.next(ESignState.VoidInProgress);
        this.void.emit();
        this.eSignHint = this.voidCorrectHint;
        break;
      case ESignState.SignatureUpdateInProgress:
        const sigUpdatePinNumber = parseInt(this.eSignForm.controls.pinNumber.value);
        this.signatureUpdate.emit(sigUpdatePinNumber);
        this.eSignHint = '';
        break;
    }
  }

  cancelClick() {
    this.confirmInProgress = false;
    this.eSignHint = '';
    this.eSignForm.reset();
    switch (this.state) {
      case ESignState.NotSigned:
        this.cancelSign.emit();
        break;
      case ESignState.VoidInProgress:
        this.cancelVoid.emit();
        break;
      default:
        //do nothing
        break;
    }
    this.currState.next(
      this.isSigned()
        ? this.currentUserHasSigned
          ? this.isSignatureInactive
            ? ESignState.SignedInactiveCurrentUser
            : ESignState.SignedActiveCurrentUser
          : ESignState.SignedOtherUser
        : ESignState.NotSigned
    );
  }

  voidAndCorrectClick() {
    switch (this.state) {
      case ESignState.SignedActiveCurrentUser:
      case ESignState.SignedInactiveCurrentUser:
      case ESignState.SignedOtherUser:
        this.confirmInProgress = true;
        if (this.legVerified) {
          this.eSignHint =
            'You cannot void the e-signature a verified leg.  Please remove verification first before voiding the e-signature the leg.';
        } else {
          this.eSignHint =
            'Are you sure you want to Void out this e-Signature? To do this you need to click on Confirm and enter your Pin';
        }

        break;
      case ESignState.VoidInProgress:
        this.cancelClick();
        break;
    }
  }

  generateConfirmMessageForCategory() {
    switch (this.signCategory) {
      case ESignCategory.AIRWORTHY:
        this.eSignHint =
          'Are you sure you want to sign the Airworthiness Release for this trip?  You are listing that this expires on the above Date, or Aircraft Total Time, or A/C Cycles.  By clicking yes you will lock the Airworthiness Release for this trip.';
        break;
      case ESignCategory.FLIGHTLOG:
        this.eSignHint =
          'Are you sure you want to Sign Off on this flight log?  Signing will lock down the leg for this trip, preventing further edits.  Please review the data elements on this flight record before confirming the Sign Off.';
        break;
      case ESignCategory.SQUAWK:
        this.eSignHint =
          'Are you sure you want to Sign Off on this Squawk/Discrepancy?  Signing will lock it down, preventing further edits.  Please review before confirming the Sign Off of this Squawk/Discrepancy.';
        break;
      case null:
      default:
        this.eSignHint =
          'Are you sure you want to sign off on this record?  This will lock the record and prevent future edits.';
    }
  }

  confirmSignatureUpdate() {
    this.eSignHint = this.signatureUpdateHint;
    this.confirmInProgress = true;
    this.currState.next(ESignState.SignatureUpdateInProgress);
  }

  revisionClick() {
    if (this.hasHistory) {
      this.showHistory = !this.showHistory;
      this.historyOpen.emit(this.showHistory);
    }
  }

  navigateToUserSignatureProfile() {
    this.router.navigateByUrl('/profile/user-signature');
    this.routeToProfile.emit();
  }

  promptLeaveToProfile() {
    this.promptConfirmLeave$()
      .afterClosed()
      .pipe(
        untilViewDestroyed(this),
        filter(rsp => Boolean(rsp)),
        switchMap(() => {
          this.navigateToUserSignatureProfile();
          return of(EMPTY);
        })
      )
      .subscribe();
  }
}
